import React from 'react';

import { formatDecimal } from '@/lib/utils';

import { TableBasicInput } from './table-basic-input';

export interface TablePercentageInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  hasError?: boolean;
  clampRange?: {
    min: number;
    /** Optional upper bound applied to the value the user *enters* (not to the
     * displayed value). chooseDiscount and marginPercentage both use 0-100: a
     * discount or a margin on the selling price cannot exceed 100%, and the
     * backend's `Percentage` value object rejects fractions > 1 (it surfaced as
     * "Expected percentage, got 21.51" when an out-of-range margin slipped
     * through). Out-of-range values already stored in the data are still
     * displayed as-is; only entry is clamped. Leave `max` undefined for inputs
     * that genuinely have no ceiling. */
    max?: number;
  };
}

/**
 * Specialized percentage input for table inline editing
 * Handles percentage formatting, validation, and optional clamping
 */
export const TablePercentageInput: React.FC<TablePercentageInputProps> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  disabled,
  autoFocus,
  className,
  hasError = false,
  clampRange,
}) => {
  return (
    <div className="relative flex h-full w-full items-center">
      <TableBasicInput
        value={
          value !== null && value !== undefined ? formatDecimal(value) : ''
        }
        onChange={(strValue) => {
          if (strValue === null) {
            onChange(null);
          } else {
            const val = strValue.replace(',', '.');
            let parsed = isNaN(parseFloat(val)) ? null : parseFloat(val);

            // Apply clamping if specified
            if (parsed !== null && clampRange) {
              if (clampRange.max !== undefined && parsed > clampRange.max)
                parsed = clampRange.max;
              if (parsed < clampRange.min) parsed = clampRange.min;
            }

            if (parsed !== value) {
              onChange(parsed);
            }
          }
        }}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        className={className}
        hasError={hasError}
        align="right"
        onInputChange={(e) => {
          // Allow only numbers, comma, and dot. The minus sign is intentionally
          // stripped so users can never *enter* a negative percentage. Existing
          // negative values coming from the backend are still readable (rendered
          // via `formatDecimal` in the field's value) and can be corrected: as
          // soon as the user edits the cell the leading "-" is removed here.
          // Uses the global flag so every invalid character is removed (e.g. a
          // pasted "-1-5" becomes "15" instead of keeping a stray dash).
          const sanitized = e.target.value.replace(/[^0-9.,]/g, '');
          if (sanitized !== e.target.value) {
            e.target.value = sanitized;
          }
        }}
      />
      <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 select-none text-base text-gray-500">
        %
      </span>
    </div>
  );
};
